/**
 * Script Runner
 *
 * Executes pre-request and post-request scripts in a sandboxed Web Worker.
 * The Worker provides real isolation: no access to DOM, localStorage, cookies,
 * or the parent window's scope. Dangerous APIs (fetch, XMLHttpRequest, etc.)
 * are explicitly blocked inside the worker.
 *
 * Falls back to a restricted Function-based execution if Workers are unavailable.
 */

import {
  ScriptExecutionContext,
  ScriptResult,
  createPmApi,
  createScriptConsole,
} from './scriptContext';
import { executeInWorker, isWorkerAvailable, WorkerScriptContext } from './sandboxWorker';

/**
 * Convert ScriptExecutionContext to the serializable format expected by the worker.
 */
function toWorkerContext(context: ScriptExecutionContext): WorkerScriptContext {
  return {
    request: {
      id: context.request.id,
      name: context.request.name,
      url: context.request.url,
      method: context.request.method,
      headers: context.request.headers || [],
      body: context.request.body,
    },
    response: context.response
      ? {
          status: context.response.status,
          statusText: context.response.statusText,
          headers: context.response.headers,
          body: context.response.body,
          time: context.response.time,
          size: context.response.size,
        }
      : undefined,
    environment: context.environment
      ? { variables: (context.environment.variables || []).map(v => ({ key: v.key, value: v.value, enabled: v.enabled })) }
      : undefined,
    globalEnvironment: context.globalEnvironment
      ? { variables: (context.globalEnvironment.variables || []).map(v => ({ key: v.key, value: v.value, enabled: v.enabled })) }
      : undefined,
    collectionVariables: context.collectionVariables?.map(v => ({ key: v.key, value: v.value, enabled: v.enabled })),
  };
}

/**
 * Execute a script string in a sandboxed Web Worker.
 * Falls back to restricted Function execution if Workers are unavailable.
 */
export async function executeScript(
  script: string,
  context: ScriptExecutionContext,
  timeout: number = 5000
): Promise<ScriptResult> {
  const emptyResult: ScriptResult = {
    success: true,
    logs: [],
    tests: [],
    variableUpdates: {
      environment: {},
      collection: {},
      global: {},
    },
  };

  if (!script || !script.trim()) {
    return emptyResult;
  }

  // Use Web Worker sandbox if available (normal browser environment)
  if (isWorkerAvailable()) {
    const workerContext = toWorkerContext(context);
    const workerResult = await executeInWorker(script, workerContext, timeout);
    return workerResult as ScriptResult;
  }

  // Fallback: restricted Function execution (e.g., test environments without Workers)
  return executeFallback(script, context, timeout);
}

/**
 * Fallback execution using Function constructor with restricted scope.
 * Used when Web Workers are not available.
 */
async function executeFallback(
  script: string,
  context: ScriptExecutionContext,
  timeout: number
): Promise<ScriptResult> {
  const result: ScriptResult = {
    success: true,
    logs: [],
    tests: [],
    variableUpdates: {
      environment: {},
      collection: {},
      global: {},
    },
  };

  try {
    const pm = createPmApi(context, result);
    const scriptConsole = createScriptConsole(result);

    // Block access to dangerous globals by shadowing them as parameters
    const wrappedScript = `
      "use strict";
      return (async function(pm, console, window, document, localStorage, sessionStorage, fetch, XMLHttpRequest, WebSocket, EventSource, eval) {
        ${script}
      })(pm, console, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined);
    `;

    const fn = new Function('pm', 'console', wrappedScript);

    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Script execution timeout')), timeout)
    );

    await Promise.race([fn(pm, scriptConsole), timeoutPromise]);
  } catch (error) {
    result.success = false;
    result.error = error instanceof Error ? error.message : String(error);
  }

  return result;
}

/**
 * Execute a pre-request script
 * Can modify the request before it's sent
 */
export async function executePreRequestScript(
  script: string,
  context: ScriptExecutionContext
): Promise<ScriptResult> {
  return executeScript(script, context);
}

/**
 * Execute a post-request script (tests)
 * Can access the response and run assertions
 */
export async function executePostRequestScript(
  script: string,
  context: ScriptExecutionContext
): Promise<ScriptResult> {
  return executeScript(script, context);
}

export type { ScriptResult, ScriptExecutionContext };
